// client/src/pages/admin/AdminLogin.jsx - FIXED VERSION
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AdminAuthLayout from '../../components/admin/layout/AdminAuthLayout';
import '../../styles/components/admin/AdminAuth.css';
import neuroLexLogo from '../../assets/logo/neurolex_logo_new2.png';

import { ReactComponent as EyeIcon } from '../../assets/icons/indicators/eye_open_icon.svg';
import { ReactComponent as EyeOffIcon }  from '../../assets/icons/indicators/eye_closed_icon.svg';


// FIXED: Use the correct API URL
const API_URL = process.env.REACT_APP_API_URL || '/api';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleRememberMe = (e) => {
    setRememberMe(e.target.checked);
  };

  // Validate email format
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "Email is required";
    if (!regex.test(email)) return "Please enter a valid email address";
    return "";
  };

  // Validate password
  const validatePassword = (password) => {
    if (!password) return "Password is required";
    return "";
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password)
    };
    
    setFormErrors(errors);
    return !errors.email && !errors.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form inputs
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError('');

    try {
      console.log('🔧 Attempting admin login with:', { email: formData.email });
      
      // FIXED: Use the correct admin endpoint that actually exists
      const response = await axios.post(`${API_URL}/admin/login`, formData);
      
      console.log('✅ Login response:', response.data);
      
      if (response.data.success) {
        const { token, user } = response.data;
        
        // FIXED: Store authentication data with admin-specific keys
        localStorage.setItem('adminToken', token);
        localStorage.setItem('adminUser', JSON.stringify(user));
        
        // IMPORTANT: Also set regular token for API compatibility
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Set token expiry
        if (rememberMe) {
          const expiryDate = new Date();
          expiryDate.setDate(expiryDate.getDate() + 30); // 30 days
          localStorage.setItem('tokenExpiry', expiryDate.toISOString());
        } else {
          const expiryDate = new Date();
          expiryDate.setHours(expiryDate.getHours() + 12); // 12 hours
          localStorage.setItem('tokenExpiry', expiryDate.toISOString());
        }
        
        // Set up axios default header for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        console.log('✅ Token and user stored successfully');
        console.log('👤 User role:', user.role);
        
        // Navigate to admin dashboard
        setTimeout(() => {
          if (user.role === 'admin') {
            console.log('🚀 Navigating to admin dashboard');
            navigate('/admin', { replace: true }); // Changed from /admin/dashboard to /admin
          } else {
            console.log('❌ User is not admin, role:', user.role);
            setError('Access denied. Admin privileges required.');
          }
        }, 100);
        
      } else {
        console.log('❌ Login failed:', response.data.message);
        setError(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('❌ Admin login error:', error);
      
      // Better error handling
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Invalid credentials';
        console.log('❌ Server error:', errorMessage);
        setError(errorMessage);
      } else if (error.request) {
        console.log('❌ Network error');
        setError('Network error. Please check your connection.');
      } else {
        console.log('❌ Unexpected error:', error.message);
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Login form content
  const loginForm = (
    <div className="form-container">
      <div className="form-content-container"> 
      <img 
        src= {neuroLexLogo}
        alt="Neurolex" 
        className="app-logo-small" 
      />
      
      <h1 className="form-title">WELCOME BACK</h1>
      
      <p className="form-subtitle">
        This login portal is intended for <strong>authorized administrators</strong> to securely access the Neurolex system.
      </p>
      
      {error && (
        <div className="alert alert-error">
          Invalid credentials
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="admin-form-group">
          <label htmlFor="email" className="admin-form-label">Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className={`admin-form-input ${formErrors.email ? 'input-error' : ''}`}
          />
          {formErrors.email && <div className="error-message">{formErrors.email}</div>}
        </div>
        
        <div className="admin-form-group">
          <label htmlFor="password" className="admin-form-label">Password</label>
          <div className="password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              className={`admin-form-input ${formErrors.password ? 'input-error' : ''}`}
            />
            <button 
            type="button" 
            className="password-toggle-button"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? (
              <EyeOffIcon className="password-toggle-icon" />
            ) : (
              <EyeIcon className="password-toggle-icon" />
            )}
          </button>
          </div>
          {formErrors.password && <div className="error-message">{formErrors.password}</div>}
        </div>
        
        <div className="admin-remember-forgot-container">
         <div className="admin-checkbox-container">
          <label className="admin-checkbox-wrapper">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={handleRememberMe}
            />
            <span className="admin-custom-checkbox"></span>
            Remember me
          </label>
        </div>

          <Link to="/admin/forgot-password" className="forgot-password-link">
            Forgot Password?
          </Link>
        </div>
        
        <button 
          type="submit" 
          className="admin-form-button" 
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="loading-spinner"></span>
          ) : (
            'Sign In'
          )}
        </button>
      </form>
      </div>
    </div>
  );

  return (
    <AdminAuthLayout>
      {loginForm}
    </AdminAuthLayout>
  );
};

export default AdminLogin;